/* eslint-disable no-console */
import { useEffect, useMemo, useState } from 'react';
import { useMembers, useSpace } from '@ably/spaces/react';
import type { SpaceMember } from '@ably/spaces';

/**
 * Aggregates other members' Spaces locations into `Map<quoteItemId, Member[]>`
 * so each quote row can render the presence indicator for whoever is focused
 * on it. Mirrors the RFQ `useRowFocus` hook.
 */
export const useQuoteRowFocus = () => {
  const { space } = useSpace();
  const { others } = useMembers();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!space) return undefined;
    const handler = () => setTick(t => t + 1);
    space.locations.subscribe('update', handler);
    return () => {
      try {
        space.locations.unsubscribe('update', handler);
      } catch (err) {
        console.warn('[quote-spaces] locations.unsubscribe failed', err);
      }
    };
  }, [space]);

  return useMemo(() => {
    const map = new Map<string, SpaceMember[]>();
    (others || []).forEach(member => {
      const loc = member.location as { quoteItemId?: string } | null;
      const itemId = loc?.quoteItemId;
      if (!itemId) return;
      const existing = map.get(itemId) || [];
      existing.push(member);
      map.set(itemId, existing);
    });
    return map;
    // `tick` recomputes when locations fire, in case `others` is referentially
    // stable across a location-only update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [others, tick]);
};
